/**
 * „Neue Kampagne“ (Recruiting) — Vertrag zwischen Wizard-Oberfläche und API.
 *
 * Quelle der Wahrheit für die Form der Daten. Das Backend (`packages/api/src/modules/campaign-wizard`)
 * spiegelt diese Typen als zod-Schemas; ändert sich hier etwas, ändert es sich dort mit.
 *
 * Alle Endpunkte liegen unter `/api/v1/campaign-wizard` (Mandant und Nutzer kommen aus dem Token):
 *
 *   Ohne Kampagne
 *   POST   /campaign-wizard/briefing/check              { text, extras? } → AdCheckResult
 *   POST   /campaign-wizard/briefing/read-url           { url }                      → { text, jobTitle? }
 *   POST   /campaign-wizard/briefing/derive             { text }                     → BriefingDerivation
 *   GET    /campaign-wizard/criteria-templates                                       → { templates: CriteriaTemplate[] }
 *   POST   /campaign-wizard/criteria-templates          { name, criteria }           → CriteriaTemplate   (für das Team speichern)
 *   POST   /campaign-wizard/criteria-from-briefing      { text }                     → { criteria: CriteriaSet, source }
 *   GET    /campaign-wizard/blacklists                                               → { lists: WizardBlacklistList[] }
 *   POST   /campaign-wizard/blacklists/import           { name, type, entries[] }    → WizardBlacklistList
 *   POST   /campaign-wizard/campaigns                   { name, accountId, briefing, cloneFromCampaignId?, cloneContacts? } → { campaignId, state }
 *
 *   Mit Kampagne (`:id` = Kampagnen-ID, Entwurf)
 *   GET    /campaign-wizard/:id                                                      → WizardSnapshot
 *   PUT    /campaign-wizard/:id/state                { state, step }                 → WizardSnapshot   (schreibt den Zustand und projiziert ihn in die Kampagne)
 *   POST   /campaign-wizard/:id/search/estimate      { locations, criteria, method, searchUrl? } → SearchEstimate
 *   POST   /campaign-wizard/:id/search/start                                         → { started: boolean, message? }  (legt die Suchen an, startet den Import der ersten Kandidaten)
 *   GET    /campaign-wizard/:id/candidates           ?filter=&judgement=             → WizardCandidateList
 *   GET    /campaign-wizard/:id/candidates/:cid                                      → WizardCandidateDetail
 *   PATCH  /campaign-wizard/:id/candidates/:cid      { active?, judgement? | null }  → WizardCandidate
 *   POST   /campaign-wizard/:id/candidates/:cid/blacklist  { scope: 'person'|'company', target: 'campaign'|<listId> } → WizardCandidateList
 *   DELETE /campaign-wizard/:id/candidates/:cid/blacklist  ?scope=person|company     → WizardCandidateList
 *   GET    /campaign-wizard/:id/candidates/:cid/export                               → CSV (Datei)
 *   POST   /campaign-wizard/:id/flow/plan            { answers }                     → FlowPlanResult
 *   POST   /campaign-wizard/:id/flow/test            { candidateId } → FlowTestStatusResponse (GET ?candidateId&testId fragt den Lauf ab)
 *   POST   /campaign-wizard/:id/start                                                → RunSnapshot  (validiert, startet die Kampagne)
 *   GET    /campaign-wizard/:id/run                                                  → RunSnapshot
 */

export type WizardStep = 1 | 2 | 3 | 4 | 5 | 6 | 7;
export const WIZARD_STEP_COUNT = 7;

/* ------------------------------------------------------------------ */
/* Schritt 1 — Stelle                                                  */
/* ------------------------------------------------------------------ */

export type AdCheckLevel = 0 | 1 | 2 | 3; // 0 = leer, 1 = zu dünn, 2 = ausbaufähig, 3 = gut

export interface AdCheckItem {
  /** Einer der 6 festen Punkte, siehe AD_CHECK_LABELS. */
  label: string;
  ok: boolean;
  comment: string;
  /** Satz, der an die Anzeige angehängt werden kann (nur wenn !ok), sonst ''. Beginnt mit einem Leerzeichen. */
  add: string;
}

export interface AdCheckResult {
  level: AdCheckLevel;
  /** Anzahl erfüllter Punkte (0–6). */
  score: number;
  total: number;
  summary: string;
  items: AdCheckItem[];
  /** `ai` = von der KI geprüft, `heuristic` = Schnellprüfung („KI nicht erreichbar“). */
  source: 'ai' | 'heuristic';
}

export const AD_CHECK_LABELS = [
  'Jobtitel',
  'Ort',
  'Erfahrungslevel',
  'Fachliche Anforderungen',
  'Sprache',
  'Was ihr bietet',
] as const;

/** Radius in km: die LinkedIn-Stufen 1 / 5 / 10 / 25 / 35 / 50 / 75 / 100 Meilen (Backend: wizard-radius.ts). */
export type Radius = 1.6 | 8 | 15 | 40 | 55 | 80 | 120 | 160;
export const RADIUS_STEPS: Radius[] = [1.6, 8, 15, 40, 55, 80, 120, 160];

export interface WizardLocation {
  /** Anzeigetext des LinkedIn-Ortes, z. B. „Köln, Nordrhein-Westfalen, Deutschland“. */
  city: string;
  /** Radius in km; `null` = ganze Region (kein Radius). Backend: wizard-radius.ts. */
  radius: Radius | null;
  /** LinkedIn-Orts-ID aus der Ortssuche; fehlt bei alten Entwürfen und Vorschlägen (Chip „bitte bestätigen“). */
  geoUrn?: string;
  /** Bundesland / Land / Region / Market Area: wird nie mit Radius gesucht. */
  region?: boolean;
  /**
   * „PLZ + Umkreis“ (nur Sales Navigator und Recruiter): die PLZ (bzw. der Ortsname, wenn LinkedIn keine PLZ liefert). Dann ist
   * `geoUrn` die ID des LinkedIn-PLZ-Eintrags und `radius` immer eine Stufe (nie `null`). Fehlt bei allen Orten ohne PLZ-Suche.
   */
  postalCode?: string;
}

/** Ein Vorschlag der LinkedIn-Ortssuche (`POST /linkedin/lookups/geo`). */
export interface GeoOption {
  id: string;
  text: string;
  /** Aus dem Anzeigetext abgeleitet: Region statt Stadt. */
  region: boolean;
}

/** Aus dem Briefing abgeleitete Suchkriterien (Schritt 2, Zeilen unter „Orte“). */
export interface SearchCriteria {
  jobTitles: string[];
  /**
   * „Weitere Suchparameter“ (Schlüssel historisch `experience`): Stichworte, die als Keywords in die LinkedIn-Suche gehen
   * (höchstens 5); Jahres-Chips wie „5+ Jahre“ filtert LinkedIn nicht, sie fließen nur in die KI-Bewertung ein.
   */
  experience: string[];
  languages: string[];
  /** Branchen (deutsche LinkedIn-Bezeichnungen aus `industries.ts`). */
  industries: string[];
}

export interface BriefingDerivation {
  jobTitle: string;
  /** Vorschlag für den Kampagnennamen, z. B. „Senior Backend Entwickler (m/w/d) – München“. */
  name: string;
  locations: WizardLocation[];
  criteria: SearchCriteria;
  source: 'ai' | 'heuristic';
}

/* ------------------------------------------------------------------ */
/* Schritt 3 — KI-Bewertung                                            */
/* ------------------------------------------------------------------ */

export interface CriteriaSet {
  must: string[];
  nice: string[];
  excl: string[];
}

/**
 * Produktionsregel der KI-Bewertung (drei Klassen, überall gleich):
 *  A = Muss-Kriterien erfüllt (belegt oder aus Rollen/Erfahrung klar wahrscheinlich),
 *  B = grundsätzlich geeignet für die Stelle, Muss-Kriterien aber nicht (voll) belegt,
 *  C = No-Go trifft zu oder klar nicht geeignet für die Stelle – wird nicht angesprochen (seit 18.10.2026, #669).
 */
export type JudgementClass = 'A' | 'B' | 'C';
export type Judgement = JudgementClass;

export interface CriteriaTemplate {
  id: string;
  name: string;
  /** Anzeige unter dem Namen, z. B. „Team · in 6 Kampagnen genutzt“ / „Meine Vorlage · in 2 Kampagnen genutzt“. */
  meta: string;
  scope: 'team' | 'own';
  usedIn: number;
  criteria: CriteriaSet;
}

/* ------------------------------------------------------------------ */
/* Blacklists                                                          */
/* ------------------------------------------------------------------ */

export interface WizardBlacklistList {
  id: string;
  name: string;
  type: 'company' | 'contact';
  /** Anzahl Einträge (Firmen bzw. Kontakte). */
  count: number;
  /** Standardmäßig für neue Kampagnen aktiv. Derzeit immer false: der Nutzer wählt selbst. */
  defaultOn: boolean;
}

/* ------------------------------------------------------------------ */
/* Wizard-Zustand (Schritt 1–7), wird an der Kampagne gespeichert      */
/* ------------------------------------------------------------------ */

export type SearchMethod = 'saia' | 'url';
export type Audience = 'new' | 'existing' | 'both';
export type RecruitsFor = 'own' | 'clients';
export type PrimaryLanguage = 'de' | 'en' | 'auto';
export type SecondaryLanguage = '' | 'de' | 'en';

export interface WizardCustomPlaceholder {
  key: string;
  value: string;
}

/** Vom Wizard selbst geführte Platzhalter: nicht als eigene Platzhalter bearbeitbar. */
export const RESERVED_PLACEHOLDER_KEYS = ['meetingLink', 'referralBonus'] as const;

export interface WizardAutoReplyCase {
  promptToIdentify: string;
  promptToReply: string;
}

export interface WizardAutoReply {
  isActive: boolean;
  /** Ziel der Kommunikation (`autoReply.goalofCommunication`). */
  goalofCommunication: string;
  /** Fallbehandlung (`autoReply.caseHandling`). */
  caseHandling: WizardAutoReplyCase[];
  /** Nutzer hat Ziel oder Fälle bearbeitet: dann folgen sie nicht mehr den Vorgaben der gewählten Sprache. Wird nicht in die Kampagne geschrieben. */
  custom?: boolean;
}

/** Vorgaben (`auto-reply-defaults.ts`) für Recruiting je Sprache; kommt mit dem Snapshot. */
export type WizardAutoReplyDefaults = Record<'de' | 'en', { goalofCommunication: string; caseHandling: WizardAutoReplyCase[] }>;

export interface WizardState {
  version: 1;
  /** Schritt, bis zu dem der Nutzer gekommen ist / an dem er zuletzt war. */
  step: WizardStep;
  /** Höchster erreichter Schritt (Server führt `max`, begrenzt auf 1 bei ungültiger Prüfung der Anzeige). */
  maxStep?: WizardStep;

  // 1 — Stelle
  briefing: string;
  /** „Ergänzungen & Optimierungen“: zweites Feld unter der Anzeige (Vorschläge der Prüfung, eigene Fakten). Wird nicht 1:1 an die Talente geschickt; SAIA nutzt Anzeige und Ergänzungen als Grundlage für Suche, Bewertung und Nachrichten. */
  briefingExtras: string;
  name: string;
  /** Jobtitel für `{{jobTitle}}` in Nachrichten und Suche. Leer = aus den Suchkriterien bzw. der Anzeige abgeleitet. */
  jobTitle: string;
  /** Signatur (Anzeige + Ergänzungen) der letzten Ableitung der Suchkriterien. Weicht sie vom aktuellen Text ab, bietet Schritt 2 die Neuableitung an. Leer = unbekannt. */
  derivedKey: string;
  accountId: string;
  adCheck: (AdCheckResult & { checkedText: string; checkedHash?: string }) | null;

  // 2 — Kandidaten
  method: SearchMethod;
  locations: WizardLocation[];
  criteria: SearchCriteria;
  /** Erster eigener Link (Altbestand und Anzeige); maßgeblich ist `searchUrls`, sobald gesetzt. */
  searchUrl: string;
  /** Eigene Suchlinks, höchstens {@link MAX_SEARCH_LINKS}. Leer = nur `searchUrl` (Entwürfe vor mehreren Links). */
  searchUrls: string[];
  /** Schlüssel = Blacklist-ID, Wert = für diese Kampagne aktiv. */
  globalBlacklists: Record<string, boolean>;
  blacklistCompanies: string[];
  blacklistPeople: string[];

  // 3 — KI-Bewertung
  templateId: string;
  templateName: string;
  judging: CriteriaSet;
  /** Kriterien entsprechen der Vorlage (sonst „angepasst“). */
  templateSaved: boolean;
  classes: Record<JudgementClass, boolean>;
  /** Ohne KI-Bewertung alle Kandidaten ansprechen — nur bei eigenen Suchlinks wirksam (siehe `judgingSkipped`). */
  skipJudging: boolean;

  // 4 — Kandidatenliste: Überschreibungen liegen an den Kampagnen-Kontakten (siehe Kandidaten-Endpunkte)

  // 5 — Ansprache
  recruitsFor: RecruitsFor;
  audience: Audience;
  withNote: boolean;
  /** Nur Recruiter / Sales Navigator, neue Kontakte: nicht angenommene Anfrage mit EINER InMail nachfassen (sonst zurückziehen). */
  inmailFallback: boolean;
  referral: boolean;
  bonus: string;
  /** Nur bei normalem Konto relevant. */
  inmailCredit: boolean | null;
  language: PrimaryLanguage;
  language2: SecondaryLanguage;
  emojis: boolean;
  swissS: boolean;
  calendar: boolean;
  calendarUrl: string;
  /**
   * Automatische Antworten wie im alten Builder (`campaign.autoReply`: isActive, goalofCommunication, caseHandling).
   * Fehlt bei alten Entwürfen; der Server füllt es dann beim Laden aus der Kampagne bzw. den Vorgaben (aus).
   */
  autoReply?: WizardAutoReply;
  /**
   * Anrede der Nachrichten: true = Sie (Vorgabe neuer Kampagnen, wie der alte Builder), false = Du.
   * Fehlt bei Wizard-Entwürfen aus der Zeit vor der Auswahl; der Server füllt dann `false` (bisheriges Verhalten: Du).
   * Wird nach `settings.formalSpeech` geschrieben.
   */
  formalSpeech?: boolean;
  /**
   * Eigene Platzhalter wie im alten Builder (`offer.customPlaceholders`), ohne die beiden vom Wizard geführten
   * (`meetingLink` aus dem Kalenderlink, `referralBonus` aus der Prämie). Fehlt bei Altentwürfen; der Server füllt sie aus der Kampagne.
   */
  customPlaceholders?: WizardCustomPlaceholder[];

  // 6 — Nachrichten
  chatCount: 1 | 2 | 3 | 4 | 5 | 6;
  /** Eigene Wartezeiten in Minuten je Schlüssel (`FlowRow.waitKey`); fehlt ein Schlüssel, gilt die Vorgabe des Ablaufs. */
  waitMinutes?: Record<string, number>;

  // 7 — Prüfen & starten
  /** 0 = Mo–Fr 8–18, 1 = Mo–Fr 9–17, 2 = Mo–Sa 8–20, 3 = Arbeitszeiten des LinkedIn-Kontos (Vorgabe neuer Kampagnen) */
  workTime: 0 | 1 | 2 | 3;
  /** 0 = jede Nachricht freigeben, 1 = automatisch senden */
  approve: 0 | 1;
  /** Der Nutzer hat die Freigabe aktiv gewählt. Neue Entwürfe/Klone: false, Altentwürfe: undefined; Start nur bei true. */
  approveChosen?: boolean;
  /** Der Nutzer hat „Bereits Kontaktierte“ aktiv gewählt (Bedeutung wie `approveChosen`). */
  recontactChosen?: boolean;
  /** 0 = jeden Kontakt vor der ersten Ansprache freigeben, 1 = bewertete Kontakte direkt ansprechen. Fehlt bei alten Clients. */
  approveContacts?: 0 | 1;
  /**
   * Wiederansprache wie in der alten Anlage: Wartezeit in Tagen seit dem letzten Kontakt in einer anderen Kampagne
   * (`campaign.recontactCooldownDays`). 0 = keine Einschränkung (so wirkt auch ein fehlender Wert), 14/28/60/90/180 = Karenzzeit,
   * -1 (`RECONTACT_NEVER`) = nie erneut ansprechen (echter Ausschluss, `campaign.recontactNever`). Vorgabe: `companyProfile.reContactAfterDays` des Mandanten.
   */
  recontactDays: number;
}

/** Höchstzahl eigener Suchlinks (Schritt 2), wie im Backend. */
export const MAX_SEARCH_LINKS = 5;

export const WIZARD_DEFAULTS: Omit<WizardState, 'accountId'> = {
  version: 1,
  step: 1,
  briefing: '',
  briefingExtras: '',
  name: '',
  jobTitle: '',
  derivedKey: '',
  adCheck: null,
  method: 'saia',
  locations: [],
  criteria: { jobTitles: [], experience: [], languages: [], industries: [] },
  searchUrl: '',
  searchUrls: [],
  globalBlacklists: {},
  blacklistCompanies: [],
  blacklistPeople: [],
  templateId: '',
  templateName: '',
  judging: { must: [], nice: [], excl: [] },
  templateSaved: false,
  classes: { A: true, B: true, C: false },
  skipJudging: false,
  recruitsFor: 'own',
  audience: 'new',
  withNote: true,
  inmailFallback: true,
  referral: false,
  bonus: '500 €',
  inmailCredit: null,
  language: 'de',
  language2: 'en', // wie die alte Anlage (create-campaign.api.ts): Zweitsprache Englisch
  emojis: false,
  swissS: false,
  calendar: false,
  calendarUrl: '',
  chatCount: 4,
  workTime: 3,
  approve: 0,
  approveContacts: 1,
  recontactDays: 0,
};

export interface WizardAccount {
  id: string;
  name: string;
  email: string;
  /** Kontoart aus dem verbundenen LinkedIn-Konto. */
  productType: 'normal' | 'sales_navigator' | 'recruiter';
  /** Arbeitstage / -stunden des Kontos (Kontoeinstellungen); fehlen sie, lässt sich die Kampagne nicht starten. Undefined = unbekannt. */
  workDays?: string[];
  workHours?: number[];
}

export interface WizardSnapshot {
  campaignId: string;
  status: string;
  state: WizardState;
  account: WizardAccount | null;
  /** Geschätzte Treffer der Suche („ca. 1.400 Profile“); null = (noch) nicht bekannt. */
  searchEstimate: number | null;
  /** Gibt es schon eine Suche mit (zu erwartenden) Kandidaten? Fehlgeschlagen oder keine Suche = false. */
  hasSearch?: boolean;
  warnings: string[];
  /** Vorgaben der automatischen Antworten je Sprache (Ziel + Fälle). */
  autoReplyDefaults?: WizardAutoReplyDefaults;
  /** Klartext (Deutsch), wenn der Ablauf aus Schritt 5/6 nicht geschrieben werden konnte; sonst fehlt das Feld. */
  flowError?: string;
}

/** Ergebnis der Prüfung eines eigenen Suchlinks (Schritt 2). */
export type SearchLinkCheck = 'works' | 'empty' | 'broken' | 'unchecked';

export interface SearchEstimate {
  /** Nur bei eigenem Suchlink: findet er jemanden? */
  link?: SearchLinkCheck;
  /** null, wenn LinkedIn gerade nicht erreichbar ist oder die Zählung erst nach dem Start läuft. */
  estimate: number | null;
  status: 'ok' | 'unavailable';
  message?: string;
}

/* ------------------------------------------------------------------ */
/* Schritt 4 — Kandidatenliste                                         */
/* ------------------------------------------------------------------ */

export type CandidateFilter = 'all' | 'other' | 'sex' | 'blocked';

export interface WizardCandidate {
  id: string;
  name: string;
  firstName: string;
  title: string;
  company: string;
  location: string;
  linkedInUrl: string;
  /** Aktuelles Urteil (nach manueller Änderung). null = noch nicht bewertet. */
  judgement: Judgement | null;
  /** Urteil der KI vor einer manuellen Änderung. */
  aiJudgement: Judgement | null;
  manual: boolean;
  /** Steht auf der finalen Kandidatenliste. */
  active: boolean;
  blocked: boolean;
  /** Namen der Listen, auf denen Person/Firma steht („Kampagnen-Blacklist“, „Wettbewerber“, …). */
  blockedBy: string[];
  personBlockedBy: string[];
  companyBlockedBy: string[];
  inOtherCampaign: boolean;
  unclearSex: boolean;
  /**
   * Bewertungszustand: `rated` hat eine Klasse (oder manuelle Einstufung), `pending` wird bewertet,
   * `analysing` wartet auf die Profilanalyse, `waiting_quota` wartet auf das Tageskontingent der Analyse,
   * `failed` die Bewertung ist fehlgeschlagen (Grund in `ratingError`).
   */
  ratingState?: 'rated' | 'pending' | 'analysing' | 'waiting_quota' | 'waiting_linkedin' | 'failed';
  ratingError?: string;
  /** Nummer des eigenen Links, aus dem der Kandidat kommt (nur bei mehreren Links). */
  source?: number;
}

/** LinkedIn hat die Profilaufrufe des Kontos begrenzt: die Analyse pausiert, SAIA macht automatisch weiter. */
export interface WizardAnalysisPause {
  reason: 'linkedin_fuse_limit';
  /** Voraussichtlich ab (ISO). */
  resumesAt?: string;
  /** Das Konto ruft bis dahin weniger Profile ab (ISO). */
  reducedUntil?: string;
  reductionFactor?: number;
}

/** Zeiteinschätzung der Profilanalyse (Herleitung im Backend: `analysis-forecast.ts`). */
export interface WizardAnalysisForecast {
  /** Geladene Kandidaten, die noch analysiert werden müssen. */
  waiting: number;
  /** Geschätzt noch nicht geladene Treffer der laufenden Suche. */
  notLoaded: number;
  /** Mittlere Pause je Profilaufruf in Sekunden. */
  perProfileSeconds: number;
  /** Tageslimit der Profilbesuche, das heute gilt. */
  dailyLimit: number;
  usedToday: number;
  /** So viele schafft das Konto heute noch. */
  todayCount: number;
  /** So viele bleiben für spätere Tage. */
  restCount: number;
  minutesToday: number;
  /** Voraussichtlich fertig mit dem heutigen Teil (ISO). */
  etaAt: string;
  dailyLimitReached: boolean;
  /** Der Rest beginnt im nächsten Arbeitsfenster (ISO). */
  restStartsAt?: string;
  /** Voraussichtlich alles analysiert (ISO). */
  finishesOn?: string;
}

export interface WizardCandidateList {
  items: WizardCandidate[];
  counts: Record<CandidateFilter, number>;
  /** Alle Kandidaten der Vorschau (ungefiltert). */
  total: number;
  activeCount: number;
  /** Der Import/die Bewertung der ersten Kandidaten läuft noch. */
  importing: boolean;
  /** Kandidaten, deren Profil zuerst analysiert werden muss (Analyse läuft oder wartet). */
  analysisPending?: number;
  /** Das Tageskontingent für Profilanalysen ist erschöpft; die Analyse läuft später weiter. */
  analysisQuotaExhausted?: boolean;
  /** Nur gesetzt, solange das Konto gedrosselt ist und noch Kandidaten auf die Analyse warten. */
  analysisPause?: WizardAnalysisPause;
  /** Zeiteinschätzung der Analyse („X heute, Rest ab …“). */
  analysisForecast?: WizardAnalysisForecast;
  searchEstimate: number | null;
  /** Zustand der Suche: none = nie gestartet, running = lädt, finished, failed. Nur bei `running` zeigt die Liste „lädt“. */
  searchStatus?: 'none' | 'running' | 'finished' | 'failed';
  /** Klartext, wenn die Suche fehlgeschlagen ist. */
  searchError?: string;
  /** Treffer, die die am Ziel angehaltenen Suchen bei LinkedIn noch haben („Nächste 50 laden“); 0 = keine. */
  moreAvailable?: number;
  /** Herkunft je eigenem Link (nur bei mehreren Links), übersprungene Links eingeschlossen. */
  sources?: WizardLinkSource[];
}

export interface WizardLinkSource {
  /** 1-basiert, wie in Schritt 2. */
  index: number;
  url: string;
  kind: string;
  loaded: number;
  total: number | null;
  status: 'running' | 'paused' | 'finished' | 'failed' | 'skipped';
}

export interface CandidatePersona {
  key: string;
  name: string;
  /** Pfad/URL des Persona-Bildes oder null. */
  image: string | null;
  traits: string[];
  /** Motive: [Zugehörigkeit (oben), Leistung (links), Beziehung (unten), Macht (rechts)] je 0–1. */
  motives: [number, number, number, number];
  /** 0 = formell, 1 = informell. */
  style: number;
  defines: string[];
  important: string[];
  questions: string[];
  dos: string[];
  donts: string[];
  boosters: string[];
}

export interface WizardCandidateDetail extends WizardCandidate {
  initials: string;
  yearsOfExperience: number | null;
  /** Begründung der KI-Bewertung (so, wie die Bewertung sie geliefert hat); leer, solange nicht bewertet. */
  why: string;
  /** Kriterien der Kampagne (nur zur Anzeige; die KI bewertet nicht je Kriterium einzeln). */
  criteria: CriteriaSet;
  skills: { label: string; hit: boolean }[];
  experience: { role: string; at: string; when: string }[];
  companyInfo: { name: string; industry: string; size: string; location: string };
  persona: CandidatePersona | null;
}

/** Ziele im Menü „… hinzufügen zu“. */
export interface BlacklistTarget {
  /** 'campaign' oder Listen-ID. */
  target: string;
  name: string;
  meta: string;
}

/* ------------------------------------------------------------------ */
/* Schritt 5/6 — Ansprache, Nachrichten                                */
/* ------------------------------------------------------------------ */

/** Antworten, aus denen der Ablauf entsteht (Erweiterung der Flow-Builder-Antworten). */
export interface WizardFlowAnswers {
  audience: Audience;
  formalSpeech: boolean;
  language: PrimaryLanguage;
  secondaryLanguage: 'none' | 'de' | 'en';
  recruitsFor: RecruitsFor;
  withNote: boolean;
  chatCount: 1 | 2 | 3 | 4 | 5 | 6;
  referral: { enabled: boolean; bonus?: string };
  hasInMailCredit?: boolean;
  /** Recruiter / Sales Navigator: false = keine InMail nach nicht angenommener Anfrage. */
  inmailFallback?: boolean;
  emojis: boolean;
  swissS: boolean;
  calendar: { enabled: boolean; url?: string };
  waitMinutes?: Record<string, number>;
}

export type FlowRowKind = 'msg' | 'wait' | 'cond' | 'yes' | 'no' | 'end';

export interface FlowRow {
  kind: FlowRowKind;
  /** Verzweigungstiefe (Einrückung = depth × 32 px). */
  depth: number;
  /** Nummer im Kreis (nur msg). */
  n?: number;
  label: string;
  sub?: string;
  /** Rolle der Nachricht, z. B. connection_request, inmail_fallback. */
  role?: string;
  isInmail?: boolean;
  /** Nur wait/cond: Schlüssel der einstellbaren Zeit; ohne Schlüssel ist die Zeile nicht anklickbar. */
  waitKey?: string;
  /** Mit `waitKey`: aktuelle Zeit und Vorgabe in Minuten. */
  minutes?: number;
  defaultMinutes?: number;
}

export interface FlowPlanResult {
  rows: FlowRow[];
  msgCount: number;
  inmails: number;
  /** Anzahl InMails, die das Konto hergibt (0–3). */
  touches: number;
  /** Kontoinfo, z. B. „Konto: Recruiter, InMail-Guthaben“. */
  accountKnown: string;
  /** Regel-Chips für Schritt 6 („Du-Form“, „Deutsch“, …). */
  rules: string[];
  /** Fragen, die Schritt 5 noch stellen muss (z. B. InMail-Guthaben). */
  needsInMailCreditAnswer: boolean;
}

export interface FlowTestMessage {
  n: number;
  role: string;
  /** Anzeigename der Rolle, z. B. „Kontaktanfrage“. */
  type: string;
  goal: string;
  /** Wann die Nachricht rausgeht, z. B. „34 Minuten nach Annahme“, „InMail“ oder ''. */
  when: string;
  text: string;
  /** Die KI konnte den Schritt nicht schreiben (Text ist dann „(ohne Nachricht)“). */
  problem?: string;
}

/** Stand eines Flow-Tests (Server: `FlowTestStatus`). `preparing` = der Ablauf wird gerade gespeichert. */
export type FlowTestStatusResponse =
  | { status: 'preparing' }
  | { status: 'running'; testId: string }
  | { status: 'done'; testId: string; result: FlowTestResult }
  | { status: 'failed'; testId?: string; error: string };

export interface FlowTestResult {
  messages: FlowTestMessage[];
  end: string;
  /** `ai` = von der KI geschrieben, `template` = Vorlage mit Namen gefüllt („KI nicht erreichbar“). */
  source: 'ai' | 'template';
  candidateName: string;
  /** Person oder Firma steht auf einer Blacklist: wird in der Kampagne nicht angesprochen (Test läuft trotzdem). */
  blocked?: boolean;
}

/* ------------------------------------------------------------------ */
/* „Kampagne gestartet“                                                */
/* ------------------------------------------------------------------ */

export interface RunStep {
  /** check_circle | autorenew | radio_button_unchecked */
  state: 'done' | 'active' | 'pending';
  title: string;
  sub: string;
  /** Fortschritt 0–100, nur bei aktiven Schritten mit Balken. */
  progress?: number;
  approveButton?: boolean;
}

export interface RunSnapshot {
  campaignId: string;
  name: string;
  status: string;
  accountName: string;
  steps: RunStep[];
  stats: { found: number; fit: number; contacted: number };
  pendingApproval: number;
  /** Zeilen der Karte „So arbeitet der Agent“. */
  settings: { icon: string; text: string }[];
}

/* ------------------------------------------------------------------ */
/* Konstanten der Oberfläche                                           */
/* ------------------------------------------------------------------ */

export const WORK_TIMES = ['Mo–Fr, 8–18 Uhr', 'Mo–Fr, 9–17 Uhr', 'Mo–Sa, 8–20 Uhr'] as const;
export const APPROVE_OPTIONS = ['Jede Nachricht vorher freigeben', 'Nachrichten automatisch senden'] as const;
/** Wiederansprache: „nie erneut ansprechen“ (echter Ausschluss, `campaign.recontactNever`). */
export const RECONTACT_NEVER = -1;
/** Auswahl wie in der alten Anlage (0/14/28/60/90/180 Tage) plus „nie erneut“; 0 = keine Einschränkung. */
export const RECONTACT_PRESETS = [0, 14, 28, 60, 90, 180, RECONTACT_NEVER] as const;
